// D2 — Web Documentation: canonical user-corpus loader (TechSpec §29, §14).
//
// The SINGLE source of truth is `docs/content/user/*.md`. This module turns
// that corpus into in-memory articles at build time via Vite's
// `import.meta.glob(..., { as: "raw" })` — a deterministic build artifact of
// the corpus, NOT a hand-copied second copy (D2-A10/D2-A11). There is no
// separate frontend docs-copy and no runtime fetch of the corpus.
//
// Frontmatter parsing mirrors scripts/docs_validate.py (the D1 validator) so
// the rendered fields stay consistent with what CI validates.

import type { DocArticle, DocPlatform, DocStatus } from "./types";

// Human labels for the user taxonomy sections (TechSpec §11). Section identity
// (slug 2nd segment) comes from the corpus; only the display label lives here,
// so the taxonomy itself is never duplicated in the article content.
export const SECTION_LABELS: Record<string, string> = {
  "getting-started": "Начало работы",
  chat: "Чат",
  models: "Модели",
  projects: "Проекты",
  library: "Библиотека",
  agents: "Агенты",
  tariffs: "Тарифы",
  account: "Аккаунт",
  security: "Безопасность",
  support: "Техподдержка",
  troubleshooting: "Устранение неполадок",
  faq: "FAQ",
  "mobile-app": "Мобильное приложение",
  guide: "Помощник RealityAI",
};

export function sectionLabel(section: string): string {
  return SECTION_LABELS[section] ?? section;
}

const ALLOWED_PLATFORMS: DocPlatform[] = ["web", "android", "web+android"];
const ALLOWED_STATUSES: DocStatus[] = ["current", "deprecated", "future"];

// ---- Frontmatter (mirrors scripts/docs_validate.py parse_frontmatter) ----
function parseFrontmatter(text: string): { meta: Record<string, unknown>; body: string } | null {
  const lines = text.split("\n");
  if (lines[0]?.trim() !== "---") return null;
  let end = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === "---") {
      end = i;
      break;
    }
  }
  if (end === -1) return null;
  const meta: Record<string, unknown> = {};
  const strip = (v: string) => v.trim().replace(/^(['"])(.*)\1$/, "$2");
  const splitList = (v: string) => {
    const inner = v.trim().replace(/^\[|\]$/g, "");
    if (!inner.trim()) return [];
    return inner.split(",").map((s) => strip(s)).filter(Boolean);
  };
  const coerce = (raw: string): unknown => {
    const v = raw.trim();
    if (v.startsWith("[") && v.endsWith("]")) return splitList(v);
    if (v === "true") return true;
    if (v === "false") return false;
    return strip(v);
  };
  for (let i = 1; i < end; i++) {
    const line = lines[i];
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const m = line.match(/^([A-Za-z_][A-Za-z0-9_]*):\s*(.*)$/);
    if (!m) continue;
    meta[m[1]] = coerce(m[2]);
  }
  const body = lines.slice(end + 1).join("\n").replace(/^\n+/, "");
  return { meta, body };
}

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  return fallback;
}
function asInt(v: unknown, fallback = 1): number {
  const n = typeof v === "number" ? v : parseInt(asString(v, "0"), 10);
  return Number.isFinite(n) ? n : fallback;
}
function asPlatforms(v: unknown): DocPlatform[] {
  const arr = Array.isArray(v) ? v : typeof v === "string" ? [v] : [];
  return arr.filter((p): p is DocPlatform => ALLOWED_PLATFORMS.includes(p as DocPlatform));
}
function asStatus(v: unknown): DocStatus {
  const s = asString(v, "current") as DocStatus;
  return ALLOWED_STATUSES.includes(s) ? s : "current";
}
function asSlugList(v: unknown): string[] {
  const arr = Array.isArray(v) ? v : typeof v === "string" ? [v] : [];
  return arr.map((x) => asString(x)).filter(Boolean);
}

// Build one article from a raw markdown file (frontmatter + body).
export function parseArticle(slug: string, raw: string): DocArticle | null {
  const parsed = parseFrontmatter(raw);
  if (!parsed) return null;
  const m = parsed.meta;
  const fmSlug = asString(m["slug"]);
  const section = asString(m["section"]) || slug.split("/")[1] || "";
  return {
    slug: fmSlug || slug,
    title: asString(m["title"], slug),
    section,
    description: asString(m["description"]),
    status: asStatus(m["status"]),
    version: asInt(m["version"], 1),
    last_reviewed: asString(m["last_reviewed"]),
    platforms: asPlatforms(m["platforms"]),
    related: asSlugList(m["related"]),
    body: parsed.body,
  };
}

// ---- Deterministic corpus (build-time, from the canonical user domain) ----
// Keys are repo-root-relative markdown paths under docs/content/user/.
const RAW_MODULES = import.meta.glob(
  "../../../../docs/content/user/**/*.md",
  { query: "?raw", import: "default", eager: true }
) as Record<string, string>;

const corpusBySlug: Record<string, DocArticle> = {};
for (const [path, raw] of Object.entries(RAW_MODULES)) {
  // path looks like "../../docs/content/user/chat/overview.md" — derive the
  // canonical slug (== the slug declared in frontmatter, enforced by the D1
  // validator) as the relative path under docs/content.
  const idx = path.lastIndexOf("docs/content/");
  const rel = idx >= 0 ? path.slice(idx + "docs/content/".length) : path;
  const slug = rel.replace(/\.md$/, "");
  const article = parseArticle(slug, raw);
  if (article) corpusBySlug[article.slug] = article;
}

const ALL_ARTICLES: DocArticle[] = Object.values(corpusBySlug).sort((a, b) =>
  a.section === b.section ? a.title.localeCompare(b.title, "ru") : a.section.localeCompare(b.section, "ru")
);

// Test-only injection point (unit tests use a fixture corpus, not the real
// build-time glob). Never used by the app build.
let _testArticles: DocArticle[] | null = null;
export function __setTestArticles(articles: DocArticle[] | null): void {
  _testArticles = articles;
}

export interface DocsSection {
  id: string;
  label: string;
  articles: DocArticle[];
}

function activeArticles(): DocArticle[] {
  return _testArticles ?? ALL_ARTICLES;
}

/** All user-domain articles (deterministic order: section, then title). */
export function getArticles(): DocArticle[] {
  return activeArticles();
}

/** O(1) lookup by canonical slug. */
export function getArticleBySlug(slug: string): DocArticle | undefined {
  return activeArticles().find((a) => a.slug === slug);
}

/** Articles grouped into ordered sections for the landing / sidebar. */
export function getSections(): DocsSection[] {
  const bySection = new Map<string, DocArticle[]>();
  for (const a of activeArticles()) {
    const list = bySection.get(a.section) ?? [];
    list.push(a);
    bySection.set(a.section, list);
  }
  const sections: DocsSection[] = [];
  for (const [id, articles] of bySection) {
    sections.push({ id, label: sectionLabel(id), articles });
  }
  sections.sort((a, b) => a.label.localeCompare(b.label, "ru"));
  return sections;
}

/**
 * D2 search — deterministic client-side filter over the bundled corpus
 * (TechSpec §29 / Master §12 "search entry"; D4 boundary — the full
 * deterministic PostgreSQL FTS search backend is D4, NOT this).
 *
 * Matches title + description + body (case/accent-insensitive substring per
 * token, AND across tokens). `status: future` articles are EXCLUDED so search
 * never surfaces not-yet-available content as guidance (D2-A9).
 */
export function searchArticles(query: string): DocArticle[] {
  const norm = (s: string) => s.toLowerCase().normalize("NFC");
  const q = norm(query).trim();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  return activeArticles()
    .filter((a) => a.status !== "future")
    .map((a) => {
      const hay = norm(`${a.title} ${a.description} ${a.body}`);
      return { a, hit: tokens.every((t) => hay.includes(t)) };
    })
    .filter((r) => r.hit)
    .sort((x, y) => {
      const xt = norm(x.a.title).includes(norm(tokens[0])) ? 0 : 1;
      const yt = norm(y.a.title).includes(norm(tokens[0])) ? 0 : 1;
      return xt - yt || x.a.title.localeCompare(y.a.title, "ru");
    })
    .map((r) => r.a);
}
